import {
  addDays,
  GAME_NAME,
  LAUNCH_DATE,
  londonDateString,
  MAX_TOTAL,
  puzzleNumber,
  QUESTIONS_PER_ROUND,
  SPORT_PAGES,
  sportPath,
  SPORTS,
  type Challenge,
} from '@geosport/shared';
import { useEffect } from 'react';
import { Button } from '../components/Button';
import { Shell } from '../components/Shell';
import { useInstallPrompt } from '../lib/install';
import { Link, navigate } from '../lib/router';
import { useCountdown } from '../lib/useCountdown';
import { useRound } from '../store/round';
import { Results, SpecialCard } from './Results';

const STEPS = [
  {
    icon: '🌍',
    title: 'Tap where',
    text: 'Spin the globe and drop a pin where the moment happened.',
  },
  { icon: '📅', title: 'Pick when', text: 'Slide to the year. Every year off costs points.' },
  {
    icon: '📖',
    title: 'Read the story',
    text: 'Learn what really happened, then share your emoji grid.',
  },
];

function prettyDate(date: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/** A friend's "beat my score" link: today's puzzle, or a replay of an earlier one. */
function ChallengeBanner({ challenge }: { challenge: Challenge }) {
  const todayNo = puzzleNumber(londonDateString());
  const past = challenge.puzzleNo < todayNo;
  return (
    <div className="w-full rounded-2xl bg-accent/10 p-4 text-left ring-1 ring-accent/40">
      <div className="font-bold">
        🏆 A friend scored {challenge.score}/{MAX_TOTAL} on {GAME_NAME} #{challenge.puzzleNo}
      </div>
      <p className="mt-1 text-sm text-white/70">
        {past ? (
          <>
            Think you can beat it?{' '}
            <Link
              to={`/play/archive/${addDays(LAUNCH_DATE, challenge.puzzleNo - 1)}`}
              className="text-accent underline underline-offset-2"
            >
              Replay #{challenge.puzzleNo}
            </Link>{' '}
            or play today’s round.
          </>
        ) : (
          'Same five moments, same globe. Think you can beat them?'
        )}
      </p>
    </div>
  );
}

export function Home({ challenge }: { challenge?: Challenge } = {}) {
  const phase = useRound((s) => s.phase);
  const mode = useRound((s) => s.mode);
  const daily = useRound((s) => s.daily);
  const puzzleNo = useRound((s) => s.puzzleNo);
  const date = useRound((s) => s.date);
  const position = useRound((s) => s.position);
  const yesterdayAvg = useRound((s) => s.yesterdayAvg);
  const playersToday = useRound((s) => s.playersToday);
  const error = useRound((s) => s.error);
  const activeSpecials = useRound((s) => s.activeSpecials);
  const load = useRound((s) => s.load);
  const start = useRound((s) => s.start);
  const install = useInstallPrompt();
  const countdown = useCountdown();

  // Coming back from a practice round: the home screen is about today's round.
  useEffect(() => {
    if (mode !== 'daily') void useRound.getState().exitPractice();
  }, [mode]);

  // Already played today: the results screen replaces the intro (PRD §10.1).
  if (mode === 'daily' && phase === 'done') return <Results />;

  const loading = phase === 'boot' || phase === 'loading' || mode !== 'daily';
  const resuming = mode === 'daily' && phase === 'where' && position > 1;
  const doneToday = daily?.summary !== null && daily?.summary !== undefined;

  const play = (): void => {
    start();
    navigate('/play');
  };

  return (
    <Shell>
      <section className="flex flex-col items-center gap-6 py-4 text-center">
        <img src="/favicon.svg" alt="" className="h-20 w-20 animate-pop" />
        <div>
          <h1 className="text-4xl font-black tracking-tight">{GAME_NAME}</h1>
          <p className="mt-2 text-white/70">
            The daily <strong className="text-white">where + when</strong> sports quiz.
          </p>
          {/* Always rendered so the line's height is reserved before the API answers (no layout shift). */}
          <p className="mt-1 min-h-5 text-sm font-semibold text-accent" data-testid="puzzle-no">
            {puzzleNo > 0 && date
              ? `Puzzle #${puzzleNo} · ${prettyDate(date)}${
                  // Social proof once there's a crowd worth mentioning.
                  playersToday !== null && playersToday >= 10
                    ? ` · ${playersToday.toLocaleString('en-GB')} played`
                    : ''
                }`
              : '\u00a0'}
          </p>
        </div>

        {challenge && <ChallengeBanner challenge={challenge} />}

        {phase === 'error' ? (
          <div className="w-full rounded-2xl bg-red-500/10 p-4 text-sm text-red-200 ring-1 ring-red-500/30">
            <p className="mb-3">{error}</p>
            <Button variant="secondary" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        ) : (
          <Button
            onClick={play}
            disabled={loading}
            className="w-full text-lg"
            data-testid="play-button"
          >
            {loading
              ? 'Loading today’s round…'
              : resuming
                ? `Resume round (Q${position} of ${QUESTIONS_PER_ROUND})`
                : 'Play today’s round →'}
          </Button>
        )}

        <div className="grid w-full grid-cols-2 gap-3 text-sm">
          <div className="rounded-2xl bg-white/5 p-3">
            <div className="text-white/50">Yesterday’s average</div>
            <div className="text-xl font-bold tabular-nums">
              {yesterdayAvg === null ? '—' : Math.round(yesterdayAvg)}
              <span className="text-sm font-normal text-white/60">/{MAX_TOTAL}</span>
            </div>
          </div>
          <div className="rounded-2xl bg-white/5 p-3">
            <div className="text-white/50">{doneToday ? 'Next round in' : 'Round closes in'}</div>
            <div className="text-xl font-bold tabular-nums">{countdown}</div>
          </div>
        </div>

        <ol className="grid w-full gap-3 text-left">
          {STEPS.map((s, i) => (
            <li key={s.title} className="flex items-start gap-3 rounded-2xl bg-white/5 p-3">
              <span className="text-2xl" aria-hidden="true">
                {s.icon}
              </span>
              <div>
                <div className="font-semibold">
                  {i + 1}. {s.title}
                </div>
                <div className="text-sm text-white/70">{s.text}</div>
              </div>
            </li>
          ))}
        </ol>

        {activeSpecials[0] && (
          <div className="w-full">
            <SpecialCard special={activeSpecials[0]} />
          </div>
        )}

        <div className="grid w-full gap-3 text-left sm:grid-cols-2">
          <Link
            to="/practice"
            className="rounded-2xl bg-gradient-to-br from-ink-3 to-ink-2 p-4 ring-1 ring-white/10 hover:ring-accent/50"
          >
            <div className="text-lg font-bold">Practice</div>
            <div className="text-sm text-white/60">
              Random rounds by sport, or replay any previous day. Never affects your streak.
            </div>
          </Link>
          <Link
            to="/groups"
            className="rounded-2xl bg-gradient-to-br from-ink-3 to-ink-2 p-4 ring-1 ring-white/10 hover:ring-accent/50"
          >
            <div className="text-lg font-bold">Play with friends</div>
            <div className="text-sm text-white/60">
              Make a group, share the invite link and compare scores every day.
            </div>
          </Link>
        </div>

        <p className="text-sm text-white/60">
          {QUESTIONS_PER_ROUND} clues · up to {MAX_TOTAL} points · new round at midnight UK time
        </p>
      </section>

      <section className="mt-4 text-sm text-white/60">
        <h2 className="text-base font-semibold text-white">What is {GAME_NAME}?</h2>
        <p className="mt-1">
          A free daily sports geography game. Everyone gets the same five moments from football,
          cricket, Formula 1, tennis, rugby, golf and athletics. Guess the venue on a 3D globe and
          the year it happened, then compare your score with friends.{' '}
          <Link to="/how-to-play" className="text-accent underline underline-offset-2">
            How scoring works →
          </Link>
        </p>
      </section>

      {/* Server-rendered content pages, so plain links (full page loads). */}
      <section className="mt-4 text-sm text-white/60">
        <h2 className="text-base font-semibold text-white">Explore</h2>
        <p className="mt-1">
          Sports quizzes with answers:{' '}
          {SPORTS.map((s, i) => (
            <span key={s}>
              {i > 0 && ' · '}
              <a href={sportPath(s)} className="text-accent underline underline-offset-2">
                {SPORT_PAGES[s].emoji} {SPORT_PAGES[s].name}
              </a>
            </span>
          ))}
        </p>
        <p className="mt-1">
          <a href="/puzzles" className="text-accent underline underline-offset-2">
            Archive of past puzzles
          </a>{' '}
          ·{' '}
          <a href="/faq" className="text-accent underline underline-offset-2">
            FAQ
          </a>{' '}
          ·{' '}
          <a href="/compare" className="text-accent underline underline-offset-2">
            Similar games
          </a>
        </p>
      </section>

      {/* Last on purpose: Chromium only makes the site installable a few seconds after first
          paint, so a card appearing here pushes nothing but the footer (no layout shift). */}
      {(install.canInstall || install.iosHint) && (
        <section className="mt-6 rounded-2xl bg-accent/10 p-4 ring-1 ring-accent/30">
          <div className="font-bold">Get the app</div>
          <p className="text-sm text-white/70">
            {install.canInstall
              ? `Install ${GAME_NAME} on your home screen for one-tap daily rounds.`
              : 'On iPhone: tap Share, then “Add to Home Screen”.'}
          </p>
          {install.canInstall && (
            <Button className="mt-3" variant="secondary" onClick={() => void install.prompt()}>
              Install
            </Button>
          )}
        </section>
      )}
    </Shell>
  );
}
